// =============================================================================
// EMPLOYEE CONFIG
// Single source of truth for all employee fields.
//
// FIELD PROPERTIES:
//   key         - matches the backend property name (camelCase)
//   label       - display name, can be a function (f) => string for client-specific labels
//   default     - initial/reset value
//   type        - "text" | "number" | "decimal" | "date" | "boolean" | "file"
//                 defaults to "text" if not specified
//   required    - only add when true
//   feature     - only add when field is hidden for some clients e.g. "EMPLOYEELIST_VESSEL"
//   refKey      - only add when field is a dropdown linked to reference data
//   displayKey  - only add when lookup uses something OTHER than "description"
//                 can be a function (f) => string for client-specific display
//   audit       - set to false to EXCLUDE from audit trail (default is included)
//   width       - excel column width, defaults to 20
//   sampleValue - excel template sample value, can be a function (f) => string
//   dropdownList - excel dropdown config { listName, data: (refs, f) => string[] }
//                  only add when field needs an excel dropdown
// =============================================================================

export const EMPLOYEE_CONFIG = {

  // ===========================================================================
  // ROOT — flat fields on the Employee table
  // ===========================================================================
  root: {
    apiKey: null,
    fields: [
      {
        key: "employeeId",
        label: "EmployeeId",
        default: "",
        required: true,
        width: 30,
        sampleValue: "<EMP1234>",
      },
      {
        key: "lastName",
        label: "LastName",
        default: "",
        required: true,
        width: 30,
        sampleValue: "<DELA CRUZ>",
      },
      {
        key: "firstName",
        label: "FirstName",
        default: "",
        required: true,
        width: 30,
        sampleValue: "<JUAN>",
      },
      {
        key: "middleName",
        label: "MiddleName",
        default: "",
        width: 30,
        sampleValue: "<ANTONIO>",
      },
      {
        key: "extensionName",
        label: "ExtensionName",
        default: "",
        width: 30,
        sampleValue: "<OPTIONAL>",
      },
      {
        key: "nickName",
        label: "NickName",
        default: "",
        width: 20,
        sampleValue: "<OPTIONAL>",
      },
      {
        key: "age",
        label: "Age",
        default: "",
        type: "number",
        width: 10,
        sampleValue: "<30>",
      },
      {
        key: "dateOfBirth",
        label: "DateOfBirth",
        default: "",
        type: "date",
        width: 20,
        sampleValue: "<01/01/1993>",
      },
      {
        key: "placeOfBirth",
        label: "PlaceOfBirth",
        default: "",
        width: 30,
        sampleValue: "<CALOOCAN CITY>",
      },
      {
        key: "civilStatus",
        label: "CivilStatus",
        default: "",
        width: 20,
        sampleValue: "<SINGLE>",
        dropdownList: {
          listName: "CivilStatus",
          data: (refs) => refs.civilStatuses,
        },
      },
      {
        key: "bloodType",
        label: "BloodType",
        default: "",
        width: 10,
        sampleValue: "<OPTIONAL>",
      },
      {
        key: "height",
        label: "Height",
        default: "",
        width: 10,
        sampleValue: "<OPTIONAL>",
      },
      {
        key: "weight",
        label: "Weight",
        default: "",
        width: 10,
        sampleValue: "<OPTIONAL>",
      },
      {
        key: "citizenship",
        label: "Citizenship",
        default: "",
        width: 20,
        sampleValue: "<FILIPINO>",
      },
      {
        key: "religion",
        label: "Religion",
        default: "",
        width: 20,
        sampleValue: "<ROMAN CATHOLIC>",
      },
      {
        key: "gender",
        label: "Gender",
        default: "",
        width: 10,
        sampleValue: "<MALE>",
      },
      {
        key: "mobileNumber",
        label: "MobileNumber",
        default: "",
        width: 20,
        sampleValue: "<09123456789>",
      },
      {
        key: "emailAddress",
        label: "EmailAddress",
        default: "",
        width: 30,
        sampleValue: "<JUANDELACRUZ@GMAIL.COM>",
      },
      {
        key: "photo",
        label: "Photo",
        default: null,
        type: "file",
        audit: false,   // binary — excluded from audit trail
      },
      {
        key: "signature",
        label: "Signature",
        default: null,
        type: "file",
        audit: false,   // binary — excluded from audit trail
      },
      {
        key: "deviceNumber",
        label: "DeviceNumber",
        default: "",
        width: 20,
        sampleValue: "<OPTIONAL>",
      },
      {
        key: "license",
        label: "License",
        default: "",
        width: 20,
        sampleValue: "<OPTIONAL>",
      },
    ],
  },

  // ===========================================================================
  // ADDRESS — subset of root fields, grouped separately for the UI tab
  // These are still flat on the Employee table, just displayed on a different tab
  // ===========================================================================
  address: {
    apiKey: null,   // still goes on root payload, no nesting
    fields: [
      {
        key: "blkLot",
        label: "BlkLot",
        default: "",
        width: 20,
        sampleValue: "<BLK 123 LOT 456>",
      },
      {
        key: "street",
        label: "Street",
        default: "",
        width: 30,
        sampleValue: "<JUAN LUNA STREET>",
      },
      {
        key: "barangay",
        label: "Barangay",
        default: "",
        width: 20,
        sampleValue: "<DEPARO>",
      },
      {
        key: "city",
        label: "City",
        default: "",
        width: 20,
        sampleValue: "<CALOOCAN CITY>",
      },
      {
        key: "municipality",
        label: "Municipality",
        default: "",
        width: 20,
        sampleValue: "<Malvar>",
      },
      {
        key: "region",
        label: "Region",
        default: "",
        width: 20,
        sampleValue: "<CALABARZON>",
      },
      {
        key: "province",
        label: "Province",
        default: "",
        width: 20,
        sampleValue: "<BATANGAS>",
      },
      {
        key: "postalCode",
        label: "PostalCode",
        default: "",
        width: 15,
        sampleValue: "<1420>",
      },
      {
        key: "yearsOfStay",
        label: "YearsOfStay",
        default: "",
        width: 15,
        sampleValue: "<5>",
      },
    ],
  },

  // ===========================================================================
  // EMPLOYEE INFORMATION — nested object, one-to-one relation
  // ===========================================================================
  employeeInformation: {
    apiKey: "employeeInformation",
    fields: [
      {
        key: "locationId",
        label: "Location",
        default: "",
        width: 30,
        sampleValue: "<MAIN OFFICE>",
        refKey: "locations",
        dropdownList: {
          listName: "Location",
          data: (refs) => refs.locations.map((l) => l.description),
        },
      },
      {
        key: "effectiveFrom",
        label: "EffectiveFrom",
        default: "",
        type: "date",
        width: 20,
        sampleValue: "<01-01-2021>",
        excludeFromBulkUpdate: true,
      },
      {
        key: "contractFrom",
        label: "ContractFrom",
        default: "",
        type: "date",
        width: 20,
        sampleValue: "<01-01-2021>",
        excludeFromBulkUpdate: true,
      },
      {
        key: "contractTo",
        label: "Contractto",
        default: "",
        type: "date",
        width: 20,
        sampleValue: "<01-01-2022>",
        excludeFromBulkUpdate: true,
      },
      {
        key: "dateEmployed",
        label: "DateEmployed",
        default: "",
        type: "date",
        width: 20,
        sampleValue: "<01-01-2021>",
        excludeFromBulkUpdate: true,
      },
      {
        key: "dateRegular",
        label: "DateRegular",
        default: "",
        type: "date",
        width: 20,
        sampleValue: "<OPTIONAL>",
        excludeFromBulkUpdate: true,
      },
      {
        key: "dateProbationary",
        label: "DateProbationary",
        default: "",
        type: "date",
        width: 20,
        sampleValue: "<OPTIONAL>",
        excludeFromBulkUpdate: true,
      },
      {
        key: "dateResigned",
        label: "DateResigned",
        default: "",
        type: "date",
        width: 20,
        sampleValue: "<OPTIONAL>",
        excludeFromBulkUpdate: true,
      },
      {
        key: "dateContractual",
        label: "DateContractual",
        default: "",
        type: "date",
        width: 20,
        sampleValue: "<OPTIONAL>",
        excludeFromBulkUpdate: true,
      },
      {
        key: "employmentDuration",
        label: "EmploymentDuration",
        default: "",
        width: 25,
        sampleValue: "<OPTIONAL>",
        excludeFromBulkUpdate: true,
      },
      {
        key: "employmentId",
        label: "Employment",
        default: "",
        width: 25,
        refKey: "employments",
        sampleValue: "<REGULAR>",
        dropdownList: {
          listName: "Employment",
          data: (refs) => refs.employments.map((e) => e.description),
        },
      },
      {
        // label and displayKey change per client
        key: "departmentId",
        label: (f) => f.DEPARTMENT_CODE ? "DepartmentCode" : "Department",
        default: "",
        width: 30,
        refKey: "departments",
        displayKey: (f) => f.DEPARTMENT_CODE ? "code" : "description",
        sampleValue: (f) => f.DEPARTMENT_CODE ? "<DEPARTMENT_CODE>" : "<IT>",
        dropdownList: {
          listName: "Department",
          data: (refs, f) =>
            refs.departments.map((d) => f.DEPARTMENT_CODE ? d.code : d.description),
        },
      },
      {
        key: "sectionId",
        label: "Section",
        default: "",
        width: 30,
        refKey: "sections",
        sampleValue: "<SOFTWARE DEVELOPER>",
        dropdownList: {
          listName: "Section",
          data: (refs) => refs.sections.map((s) => s.description),
        },
      },
      {
        key: "classificationId",
        label: "Classification",
        default: "",
        width: 20,
        refKey: "classifications",
        sampleValue: "<OFFICE>",
        dropdownList: {
          listName: "Classification",
          data: (refs) => refs.classifications.map((c) => c.description),
        },
      },
      {
        key: "payTypeId",
        label: "PayType",
        default: "",
        width: 20,
        refKey: "payTypes",
        sampleValue: "<SEMI-MONTHLY>",
        dropdownList: {
          listName: "PayType",
          data: (refs) => refs.payTypes.map((p) => p.description),
        },
      },
      {
        // displayKey deviates — shiftName instead of description
        // label and displayKey change per client
        key: "shiftScheduleId",
        label: (f) => f.SHIFTSCHEDULE_CODE ? "ShiftScheduleCode" : "ShiftSchedule",
        default: "",
        width: 20,
        refKey: "shiftSchedules",
        displayKey: (f) => f.SHIFTSCHEDULE_CODE ? "code" : "shiftName",
        sampleValue: (f) => f.SHIFTSCHEDULE_CODE ? "<SHIFT_CODE>" : "<MORNING SHIFT>",
        masterlistKey: "shiftScheduleShiftName", // API returns shiftName not Description
        dropdownList: {
          listName: "ShiftSchedule",
          data: (refs, f) =>
            refs.shiftSchedules.map((s) => f.SHIFTSCHEDULE_CODE ? s.code : s.shiftName),
        },
      },
      {
        // label and displayKey change per client
        key: "positionId",
        label: (f) => f.POSITION_CODE ? "PositionCode" : "Position",
        default: "",
        width: 30,
        refKey: "positions",
        displayKey: (f) => f.POSITION_CODE ? "code" : "description",
        sampleValue: (f) => f.POSITION_CODE ? "<POSITION_CODE>" : "<APPLICATION DEVELOPER>",
        dropdownList: {
          listName: "Position",
          data: (refs, f) =>
            refs.positions.map((p) => f.POSITION_CODE ? p.code : p.description),
        },
      },
      {
        // displayKey deviates — "name" instead of "description"
        key: "companyId",
        label: "Company",
        default: "",
        width: 30,
        refKey: "companies",
        displayKey: "name",
        masterlistKey: "companyName", // API returns companyName not companyDescription
        sampleValue: "<COMPANY NAME>",
        dropdownList: {
          listName: "Company",
          data: (refs) => refs.companies.map((c) => c.name),
        },
      },
      {
        key: "salaryType",
        label: "SalaryType",
        default: "",
        width: 30,
        refKey: "salaryTypes",
        sampleValue: "<DAILY>",
        dropdownList: {
          listName: "SalaryTypes",
          data: (refs) => refs.salaryTypes,  // already a flat string array
        },
      },
      {
        key: "maintenanceSalaryCombinationV2Id",
        label: "MaintenanceSalaryCombination",
        default: "",
        width: 30,
        refKey: "maintenanceSalaryCombinations",
        sampleValue: "<RATE 150>",
        dropdownList: {
          listName: "MaintenanceSalaryCombinations",
           data: (refs) => (refs.maintenanceSalaryCombinations || []).map(x => x.description || ""),
        },
      },
      {
        key: "jobTitleId",
        label: "JobTitle",
        default: "",
        width: 30,
        refKey: "jobTitles",
        sampleValue: "<JOB TITLE>",
        dropdownList: {
          listName: "JobTitle",
          data: (refs) => refs.jobTitles.map((j) => j.description),
        },
      },
      {
        key: "employeeGroup1Id",
        label: "EmployeeGroup1",
        default: "",
        width: 30,
        refKey: "employeeGroups1",
        sampleValue: "<EMPLOYEE GROUP 1>",
        dropdownList: {
          listName: "EmployeeGroup1",
          data: (refs) => refs.employeeGroups1.map((g) => g.description),
        },
      },
      {
        key: "employeeGroup2Id",
        label: "EmployeeGroup2",
        default: "",
        width: 30,
        refKey: "employeeGroups2",
        sampleValue: "<EMPLOYEE GROUP 2>",
        dropdownList: {
          listName: "EmployeeGroup2",
          data: (refs) => refs.employeeGroups2.map((g) => g.description),
        },
      },
      {
        key: "plantilyaId",
        label: "Plantilla",
        default: "",
        width: 30,
        refKey: "plantilayas",
        sampleValue: "<PLANTILLA>",
        dropdownList: {
          listName: "Plantilla",
          data: (refs) => refs.plantilayas.map((p) => p.description),
        },
      },
      {
        key: "costCenterId",
        label: "CostCenter",
        default: "",
        width: 30,
        refKey: "costCenters",
        sampleValue: "<COST CENTER>",
        dropdownList: {
          listName: "CostCenter",
          data: (refs) => refs.costCenters.map((c) => c.description),
        },
      },
      {
        key: "clientId",
        label: "Client",
        default: "",
        width: 30,
        refKey: "clients",
        sampleValue: "<CLIENT>",
        dropdownList: {
          listName: "Client",
          data: (refs) => refs.clients.map((c) => c.description),
        },
      },

      // --- Feature-flagged fields ---
      // Only visible for specific clients, ignored everywhere else automatically

      {
        key: "employeeTypeId",
        label: "EmployeeType",
        default: "",
        width: 30,
        refKey: "employeeTypes",
        sampleValue: "<EMPLOYEE TYPE>",
        feature: "EMPLOYEELIST_EMPLOYEETYPE",
        dropdownList: {
          listName: "EmployeeType",
          data: (refs) => refs.employeeTypes.map((e) => e.description),
        },
      },
      {
        // displayKey deviates — "name" instead of "description"
        key: "vesselId",
        label: "Vessel",
        default: "",
        width: 40,
        refKey: "vessels",
        displayKey: "name",
        masterlistKey: "vesselName", // API returns vesselName not vesselDescription
        sampleValue: "<VESSEL>",
        feature: "EMPLOYEELIST_VESSEL",
        dropdownList: {
          listName: "Vessel",
          data: (refs) => refs.vessels.map((v) => v.name),
        },
      },
      {
        key: "destinationId",
        label: "Destination",
        default: "",
        width: 30,
        refKey: "destinations",
        sampleValue: "<DESTINATION>",
        feature: "EMPLOYEE_INFORMATION_DESTINATION",
        dropdownList: {
          listName: "Destination",
          data: (refs) => refs.destinations.map((d) => d.description),
        },
      },
    ],
  },

  // ===========================================================================
  // ACCOUNT INFORMATION — nested object, one-to-one
  // Government issued IDs
  // ===========================================================================
  employeeAccountInformation: {
    apiKey: "employeeAccountInformation",
    fields: [
      { key: "sss",        label: "Sss",        default: "", width: 30, sampleValue: "<00-00000000-0>" },
      { key: "tin",        label: "Tin",        default: "", width: 30, sampleValue: "<000-000-000-000>" },
      { key: "philhealth", label: "Philhealth", default: "", width: 30, sampleValue: "<00-000000000-0>" },
      { key: "pagibig",    label: "Pagibig",    default: "", width: 30, sampleValue: "<0000-0000-0000>" },
    ],
  },

  // ===========================================================================
  // PASSPORT DETAIL — nested object, one-to-one
  // ===========================================================================
  employeePassportDetail: {
    apiKey: "employeePassportDetail",
    fields: [
      { key: "passportNumber",    label: "PassportNumber",    default: "", width: 30, sampleValue: "<P1234567A>" },
      { key: "passportIssueDate", label: "PassportIssueDate", default: "", type: "date", width: 25, sampleValue: "<01/01/2020>" },
      { key: "passportExpiryDate",label: "PassportExpiryDate",default: "", type: "date", width: 25, sampleValue: "<01/01/2030>" },
    ],
  },

  // ===========================================================================
  // SEAMAN BOOK DETAIL — nested object, one-to-one
  // Entire section is feature-flagged
  // ===========================================================================
  employeeSeamanBookDetail: {
    apiKey: "employeeSeamanBookDetail",
    feature: "EMPLOYEE_ACCOUNT_SEAMAN_BOOK",   // entire section hidden when flag is off
    fields: [
      { key: "seamanBookNumber", label: "SeamanBookNumber", default: "", width: 30, sampleValue: "<SB0987654>" },
      { key: "bookIssueDate",    label: "BookIssueDate",    default: "", type: "date", width: 25, sampleValue: "<01/01/2020>" },
      { key: "bookExpiryDate",   label: "BookExpiryDate",   default: "", type: "date", width: 25, sampleValue: "<01/01/2030>" },
    ],
  },

  // ===========================================================================
  // LICENSE INFORMATION — nested object, one-to-one
  // Entire section is feature-flagged
  // ===========================================================================
  employeeLicenseInformation: {
    apiKey: "employeeLicenseInformation",
    feature: "EMPLOYEE_ACCOUNT_LICENSE_INFORMATION",
    fields: [
      { key: "licenseCode",       label: "LicenseCode",      default: "", width: 20, sampleValue: "<PRC>" },
      { key: "licenseNumber",     label: "LicenseNumber",    default: "", width: 30, sampleValue: "<0012345>" },
      { key: "description",       label: "Description",      default: "", width: 30, sampleValue: "<REGISTERED NURSE>" },
      { key: "expirationDate",    label: "ExpirationDate",   default: "", type: "date", width: 25, sampleValue: "<01/01/2026>" },
      {
        key: "isQualified",
        label: "IsQualified",
        default: false,
        type: "boolean",
        width: 15,
        sampleValue: "<YES>",
        dropdownList: {
          listName: "YesNoOption",
          data: () => ["YES", "NO"],
        },
      },
    ],
  },

  // ===========================================================================
  // EMPLOYEE SALARY — nested object, one-to-one
  // ===========================================================================
  employeeSalary: {
    apiKey: "employeeSalary",
    fields: [
      { key: "salary",    label: "Salary",    default: "", type: "decimal", width: 30, sampleValue: "<#,##0.00>" },
      { key: "allowance", label: "Allowance", default: "", type: "decimal", width: 30, sampleValue: "<#,##0.00>" },
    ],
  },

  // ===========================================================================
  // EMPLOYEE LEAVE — nested object, one-to-one
  // ===========================================================================
  employeeLeave: {
    apiKey: "employeeLeave",
    fields: [
      { key: "totalVacation",             label: "VacationLeave",                 default: 0, type: "decimal", width: 30, sampleValue: "<10.00>" },
      { key: "totalSick",                 label: "SickLeave",                     default: 0, type: "decimal", width: 30, sampleValue: "<10.00>" },
      { key: "totalServiceIncentive",     label: "ServiceIncentiveLeave",         default: 0, type: "decimal", width: 30, sampleValue: "<10.00>" },
      { key: "totalSoloParent",           label: "SoloParentLeave",               default: 0, type: "decimal", width: 30, sampleValue: "<10.00>" },
      { key: "vacationRemaining",         label: "VacationLeaveRemaining",        default: 0, type: "decimal", width: 30, sampleValue: "<10.00>" },
      { key: "sickRemaining",             label: "SickLeaveRemaining",            default: 0, type: "decimal", width: 30, sampleValue: "<10.00>" },
      { key: "serviceIncentiveRemaining", label: "ServiceIncentiveLeaveRemaining",default: 0, type: "decimal", width: 30, sampleValue: "<10.00>" },
      { key: "soloParentRemaining",       label: "SoloParentLeaveRemaining",      default: 0, type: "decimal", width: 30, sampleValue: "<10.00>" },
    ],
  },

  // ===========================================================================
  // EMPLOYEE STATUS — nested object, one-to-one
  // ===========================================================================
  employeeStatuses: {
    apiKey: "employeeStatuses",
    fields: [
      { key: "status", label: "EmployeeStatus", default: "", width: 30, sampleValue: "<Active>" },
    ],
  },

  // ===========================================================================
  // BANK ACCOUNTS — one-to-many array
  // ===========================================================================
  employeeBankAccounts: {
    apiKey: "employeeBankAccounts",
    type: "array",
    fields: [
      { key: "bankAccount", label: "BankAccount", default: "", width: 30, sampleValue: "<0000000000000>" },
      {
        // displayKey deviates — "name" instead of "description"
        key: "bankName",
        label: "BankName",
        default: "",
        width: 30,
        refKey: "banks",
        displayKey: "name",
        sampleValue: "<BankName>",
        dropdownList: {
          listName: "Bank",
          data: (refs) => refs.banks.map((b) => b.name),
        },
      },
      {
        key: "taxableStatus",
        label: "TaxableStatus",
        default: "",
        width: 30,
        sampleValue: "<BankStatus>",
        feature: "EMPLOYEE_ACCOUNT_EMPLOYEE_BANK_TAXABLE_STAT",
        dropdownList: {
          listName: "TaxableStatus",
          data: (refs) => refs.taxableStatuses,  // already a flat string array
        },
      },
    ],
  },

  // ===========================================================================
  // DEPENDENTS — one-to-many array
  // ===========================================================================
  employeeDependents: {
    apiKey: "employeeDependents",
    type: "array",
    fields: [
      { key: "lastName",      label: "LastName",      default: "", width: 30, sampleValue: "<DELA CRUZ>" },
      { key: "firstName",     label: "FirstName",     default: "", width: 30, sampleValue: "<JUNIOR>" },
      { key: "middleName",    label: "MiddleName",    default: "", width: 30, sampleValue: "<ANTONIO>" },
      { key: "extensionName", label: "ExtensionName", default: "", width: 30, sampleValue: "<OPTIONAL>" },
      { key: "dateOfBirth",   label: "DateOfBirth",   default: "", type: "date", width: 20, sampleValue: "<01/01/2021>" },
      { key: "gender",        label: "Gender",        default: "", width: 20, sampleValue: "<MALE>" },
      {
        key: "civilStatus",
        label: "CivilStatus",
        default: "",
        width: 20,
        sampleValue: "<SINGLE>",
        dropdownList: {
          listName: "CivilStatus",
          data: (refs) => refs.civilStatuses,
        },
      },
    ],
  },

  // ===========================================================================
  // EDUCATIONAL ATTAINMENT — one-to-many array
  // ===========================================================================
  employeeEducationAttainments: {
    apiKey: "employeeEducationAttainments",
    type: "array",
    fields: [
      { key: "level",      label: "Level",      default: "", width: 30, sampleValue: "<PRIMARY>" },
      { key: "schoolName", label: "SchoolName", default: "", width: 30, sampleValue: "<UNIVERSITY OF THE PHILIPPINES>" },
      { key: "address",    label: "Address",    default: "", width: 30, sampleValue: "<QUEZON CITY>" },
      { key: "dateFrom",   label: "DateFrom",   default: "", type: "date", width: 30, sampleValue: "<01/01/2021>" },
      { key: "dateTo",     label: "DateTo",     default: "", type: "date", width: 30, sampleValue: "<01/01/2021>" },
      { key: "course",     label: "Course",     default: "", width: 30, sampleValue: "<NONE>" },
      { key: "honors",     label: "Honors",     default: "", width: 30, sampleValue: "<NONE>" },
    ],
  },

  // ===========================================================================
  // TRAINING AND SEMINAR — one-to-many array
  // ===========================================================================
  employeeTrainingSeminars: {
    apiKey: "employeeTrainingSeminars",
    type: "array",
    fields: [
      { key: "title",    label: "Title",   default: "", width: 30, sampleValue: "<TITLE>" },
      { key: "dateFrom", label: "DateFrom",default: "", type: "date", width: 30, sampleValue: "<01/01/2021>" },
      { key: "dateTo",   label: "DateTo",  default: "", type: "date", width: 30, sampleValue: "<01/01/2021>" },
    ],
  },

  // ===========================================================================
  // EMERGENCY CONTACTS — one-to-many array
  // ===========================================================================
  employeeEmergencyContacts: {
    apiKey: "employeeEmergencyContacts",
    type: "array",
    fields: [
      { key: "name",          label: "Name",          default: "", width: 30, sampleValue: "<DELA CRUZ, SPOUSE>" },
      { key: "relationship",  label: "Relationship",  default: "", width: 30, sampleValue: "<SPOUSE>" },
      { key: "contactNumber", label: "ContactNumber", default: "", width: 30, sampleValue: "<09123456789>" },
      { key: "address",       label: "Address",       default: "", width: 30, sampleValue: "<CALOOCAN CITY>" },
    ],
  },

  // ===========================================================================
  // FAMILY DATA — one-to-many array
  // ===========================================================================
  employeeFamilyData: {
    apiKey: "employeeFamilyData",
    type: "array",
    fields: [
      { key: "name",          label: "Name",          default: "", width: 30, sampleValue: "<DELA CRUZ, SPOUSE>" },
      { key: "dateOfBirth",   label: "DateOfBirth",   default: "", type: "date", width: 30, sampleValue: "<01/01/2021>" },
      { key: "occupation",    label: "Occupation",    default: "", width: 30, sampleValue: "<NONE>" },
      { key: "contactNumber", label: "ContactNumber", default: "", width: 30, sampleValue: "<09123456789>" },
      { key: "age",           label: "Age",           default: "", type: "number", width: 30, sampleValue: "<25>" },
      { key: "address",       label: "Address",       default: "", width: 20, sampleValue: "<CALOOCAN CITY>" },
      { key: "relationship",  label: "Relationship",  default: "", width: 10, sampleValue: "<SPOUSE>" },
    ],
  },

  // ===========================================================================
  // REST DAYS — one-to-many array
  // Only used internally, not in Excel export
  // ===========================================================================
  employeeRestDays: {
    apiKey: "employeeRestDays",
    type: "array",
    fields: [
      { key: "day", label: "Day", default: "" },
    ],
  },
};